import { useState } from "react";
import { remoteUrl } from "@/services/remoteConfig";
import { useNavigate } from "react-router-dom";
import type { CloudBook } from "@/services/cloudLibraryService";
import { pullCloudBookNow } from "@/services/cloudLibraryService";

interface Props {
  /** Cloud manifest entry for a book that doesn't exist locally yet. */
  cloud: CloudBook;
  /** Shelf mode (2026-10-07) — must match BookCard's `dense` exactly or
   *  the grid rows go ragged: a placeholder was still 192x128 while a real
   *  card was 128x90, so on a mostly-cloud shelf (Thomas's iPhone, where
   *  only one book is downloaded) every tile was the wrong size AND the
   *  only one with artwork was the book he had opened. */
  dense?: boolean;
  /** Optional callback the parent can subscribe to when the download
   *  finishes — lets it refresh state if it tracks anything outside Dexie. */
  onLanded?: (bookId: string) => void;
}

/**
 * Visual twin of BookCard for books that live in the cloud but haven't been
 * pulled into local IndexedDB on this device yet. Tapping it kicks off the
 * download immediately and navigates to the reader as soon as the import
 * completes. Once it lands, the live Dexie query in LibraryView replaces
 * this placeholder with the real BookCard automatically.
 */
export function CloudPlaceholderCard({ cloud, onLanded, dense }: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coverFailed, setCoverFailed] = useState(false);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const id = await pullCloudBookNow(cloud);
      onLanded?.(id);
      navigate(`/reader/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div
      className={dense ? "relative flex flex-col" : "pv-surface pv-border relative flex flex-col rounded-md border p-3 shadow-sm"}
      style={{ opacity: busy ? 0.7 : 1 }}
    >
      <button
        onClick={onClick}
        disabled={busy}
        className={dense ? "flex flex-1 flex-col items-center gap-1 bg-transparent text-left active:opacity-80 disabled:cursor-wait" : "flex flex-1 flex-col items-center gap-3 bg-transparent text-left active:opacity-80 disabled:cursor-wait"}
      >
        <div className={dense ? "pv-surface-2 relative flex h-[128px] w-[90px] items-center justify-center overflow-hidden rounded" : "pv-surface-2 relative flex h-48 w-32 items-center justify-center overflow-hidden rounded shadow-inner"}>
          {/* ── SERVER COVER (2026-10-07) ────────────────────────────
              A cloud entry has no local blob, so its cover cannot be
              extracted on-device — which is why Thomas's iPhone showed
              artwork for exactly the one book he had downloaded. pv-lib
              now extracts and caches covers from the epubs it already
              holds, served as a ~400px thumbnail rather than full
              resolution art over the internet to a phone.
              404 is normal (Henry Moore has no image in the file at all),
              so onError falls back to the generated title cover and the
              grid stays uniform either way. */}
          {!coverFailed ? (
            <img
              src={remoteUrl(`/lib/cover/${encodeURIComponent(cloud.id)}`)}
              alt={cloud.title}
              className="h-full w-full object-cover"
              loading="lazy"
              draggable={false}
              onError={() => setCoverFailed(true)}
            />
          ) : (
            <FallbackCloudCover title={cloud.title} author={displayAuthor(cloud.author)} />
          )}
          {/* Small cloud-state badge in the top-right. */}
          <div
            className="pv-fg-soft absolute right-1 top-1 rounded-full bg-black/40 px-1.5 py-0.5 text-[10px]"
            aria-label={busy ? "Downloading from cloud" : "On cloud — tap to read"}
            title={busy ? "Downloading from cloud" : "On cloud — tap to read"}
          >
            {busy ? "…" : "☁"}
          </div>
        </div>
        {/* 2026-10-07, Thomas (twice): "remove name and author". The
            caption gate on BookCard only covered LOCAL books; a cloud
            placeholder always drew its title and author, so on a shelf
            that is mostly placeholders every tile still had text. The
            tile is the book now. */}
      </button>

      {/* 2026-10-07: on the shelf, no progress track (a cloud entry has no
          progress) and no spacer action row. Both were drawing text — a
          "·" and two invisible glyphs — on a screen whose instruction is
          "covers only". The heights now match BookCard's dense card
          because the cover box does, not because of filler rows. */}
      {!dense && (
        <>
      <div className="mt-2 flex w-full items-center gap-2">
        <div className="pv-progress-track h-1.5 flex-1 overflow-hidden rounded">
          <div className="pv-progress h-full" style={{ width: "0%" }} />
        </div>
        <span className="pv-fg-soft w-12 text-right text-sm font-medium tabular-nums">
          {busy ? "…" : "·"}
        </span>
      </div>

      {/* Empty action row to keep card heights matching BookCard. */}
      <div className="pv-fg-soft mt-2 flex items-center justify-end gap-4 text-base">
        <span className="opacity-0">✎</span>
        <span className="opacity-0">🗑</span>
      </div>
        </>
      )}

      {error && (
        <div className="mt-2 text-[10px] text-red-400">{error}</div>
      )}
    </div>
  );
}

/** Normalize "Last, First" → "First Last" for display. Mirrors the helper
 *  in BookCard so cloud placeholders and local cards render the same
 *  author string. */
function displayAuthor(raw?: string): string | undefined {
  if (!raw) return raw;
  const s = raw.trim();
  if (!s) return s;
  if (s.includes(";")) return s;
  const parts = s.split(",");
  if (parts.length !== 2) return s;
  const last = parts[0].trim();
  const first = parts[1].trim();
  if (!last || !first) return s;
  return `${first} ${last}`;
}

/** Cloud-placeholder twin of BookCard's FallbackCover — same colour
 *  hashing rule so the same book renders the same colour once it lands
 *  locally and the BookCard takes over. */
function FallbackCloudCover({ title, author }: { title: string; author?: string }) {
  const palette = ["#3b3b3b", "#5b4636", "#4a3a52", "#3a4a52", "#52473a", "#3a523f"];
  let h = 0;
  for (let i = 0; i < title.length; i++) h = ((h << 5) - h + title.charCodeAt(i)) | 0;
  const bg = palette[Math.abs(h) % palette.length];
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-center"
      style={{ background: bg, color: "#f5f0e6" }}
    >
      <span
        className="font-serif text-sm leading-tight"
        style={{ wordBreak: "break-word" }}
      >
        {title}
      </span>
      {/* author line dropped — covers only, same as BookCard */}
    </div>
  );
}
